import organizationsService from '../lib/api/organizations';
import {
  Organization,
  OrganizationMember,
  OrganizationInvitation,
} from '../types/organization';
import {
  getUserOrganizations as getUserOrganizationsFromUser,
  getUserRoleInOrganization,
  isUserOrganizationMember,
} from './userService';

// Re-export API service for backward compatibility
export { default as organizationsService } from '../lib/api/organizations';

// Helper function to map backend organization to frontend format
const mapBackendOrganizationToFrontend = (backendOrg: any): Organization => {
  return {
    id: backendOrg.id,
    name: backendOrg.name,
    createdBy: backendOrg.createdBy || backendOrg.created_by,
    createdAt: new Date(backendOrg.createdAt || backendOrg.created_at),
    logoUrl: backendOrg.logoUrl || backendOrg.logo_url,
    supportEmail: backendOrg.supportEmail || backendOrg.support_email,
    platformResources: backendOrg.platformResources || backendOrg.platform_resources,
    company: backendOrg.company,
    memberIds: backendOrg.memberIds || backendOrg.member_ids || [],
    invitations: {}, // Will be populated separately if needed
    hyperspell: backendOrg?.hyperspell ? {
      userToken: backendOrg?.hyperspell?.user_token,
      tokenExpiresAt: backendOrg?.hyperspell?.token_expires_at,
      uploadedDocs: {}
    } : undefined
  };
};

// Organization CRUD operations
export const createOrganization = async (
  name: string,
  _createdBy: string, // Not used - backend handles creator assignment
  companyUrl?: string
): Promise<Organization> => {
  try {
    // Create organization via backend API
    const response = await organizationsService.createOrganization({ name, companyUrl });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create organization');
    }

    const organization = response.data;
    
    // Generate company context if URL provided
    if (companyUrl) {
      try {
        await organizationsService.generateCompanyContext(organization.id, { url: companyUrl });
      } catch (error) {
        console.error('Failed to generate company context during organization creation:', error);
      }

      // Auto-fetch logo from domain (fire-and-forget)
      try {
        const domain = companyUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        const logoUrl = `https://img.logo.dev/${domain}?token=pk_a3-E5RCzS9CzxGENt9PrcQ&size=128&format=png`;
        const logoResponse = await fetch(logoUrl);
        if (logoResponse.ok) {
          const blob = await logoResponse.blob();
          const file = new File([blob], `${domain}-icon.png`, { type: 'image/png' });
          await uploadOrganizationLogo(organization.id, file);
        }
      } catch (error) {
        console.error('Failed to auto-fetch logo during organization creation:', error);
      }
    }

    // Map backend organization format to frontend format
    return mapBackendOrganizationToFrontend(organization);
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

export const getOrganization = async (organizationId: string): Promise<Organization | null> => {
  try {
    const response = await organizationsService.getOrganization(organizationId);
    
    if (!response.success || !response.data) {
      return null;
    }

    return mapBackendOrganizationToFrontend(response.data);
  } catch (error) {
    console.error('Error fetching organization:', error);
    return null;
  }
};

export const updateOrganization = async (
  organizationId: string,
  updates: Partial<Omit<Organization, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> => {
  try {
    // Map frontend updates to backend format
    const backendUpdates: any = {};
    
    if (updates.name !== undefined) {
      backendUpdates.name = updates.name;
    }
    if (updates.logoUrl !== undefined) {
      backendUpdates.logo_url = updates.logoUrl;
    }
    if (updates.supportEmail !== undefined) {
      backendUpdates.support_email = updates.supportEmail;
    }
    if (updates.platformResources !== undefined) {
      backendUpdates.platform_resources = updates.platformResources;
    }
    if (updates.removeHyperspell !== undefined) {
      backendUpdates.removeHyperspell = updates.removeHyperspell;
    }
    if (updates.hyperspell !== undefined) {
      backendUpdates.hyperspell = updates?.hyperspell == null ? null : {
        user_token: updates?.hyperspell?.userToken,
        token_expires_at: updates?.hyperspell?.tokenExpiresAt
      }
    }
    
    const response = await organizationsService.updateOrganization(organizationId, backendUpdates);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update organization');
    }
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// Company-specific operations
export const updateOrganizationCompany = async (
  organizationId: string,
  companyData: any
): Promise<void> => {
  try {
    // Map company data to the expected backend format
    const backendCompanyData = {
      company_name: companyData.companyName,
      help_center_url: companyData.helpCenterUrl,
      summary: companyData.summary,
      product: companyData.product,
      tone_guide: companyData.toneGuide,
      key_features: companyData.keyFeatures
    };
    
    const response = await organizationsService.updateCompanyData(organizationId, {company_data: backendCompanyData});
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update company data');
    }
  } catch (error) {
    console.error('Error updating organization company data:', error);
    throw error;
  }
};

export const generateOrganizationCompanyContext = async (
  organizationId: string,
  companyUrl: string
): Promise<void> => {
  try {
    const response = await organizationsService.generateCompanyContext(organizationId, { url: companyUrl });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to generate company context');
    }
  } catch (error) {
    console.error('Error generating organization company context:', error);
    throw error;
  }
};

// Get count of organizations a user belongs to
export const getUserOrganizationCount = async (userId: string): Promise<number> => {
  try {
    const organizations = await getUserOrganizationsFromUser(userId);
    return organizations.length;
  } catch (error) {
    console.error('Error getting user organization count:', error);
    return 0;
  }
};

// Organization deletion with cascading cleanup
export const deleteOrganization = async (organizationId: string): Promise<void> => {
  try {
    // The backend handles all cascading deletion logic
    const response = await organizationsService.deleteOrganization(organizationId);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete organization');
    }
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};

// Organization Member operations
export const addOrganizationMember = async (
  organizationId: string,
  userId: string,
  role: 'admin' | 'member'
): Promise<OrganizationMember> => {
  try {
    // Note: Backend API expects email, but we're receiving userId
    // This is a limitation that would need to be addressed in the backend
    // For now, we'll try to use the organizations API
    const response = await organizationsService.addMember(organizationId, {
      email: userId, // This may not work - backend expects email
      role
    });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to add organization member');
    }

    // Map backend member to frontend format
    const backendMember = response.data;
    return {
      id: backendMember.user_id,
      organizationId: backendMember.organization_id,
      userId: backendMember.user_id,
      role: backendMember.role,
      joinedAt: new Date(backendMember.joined_at),
    } as OrganizationMember;
  } catch (error) {
    console.error('Error adding organization member:', error);
    throw error;
  }
};

export const getOrganizationMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
  try {
    const response = await organizationsService.getOrganizationMembers(organizationId);
    
    if (!response.success || !response.data) {
      return [];
    }

    // Map backend members to frontend format
    return response.data.members.map(backendMember => ({
      id: backendMember.user_id,
      organizationId: backendMember.organization_id,
      userId: backendMember.user_id,
      role: backendMember.role === 'owner' ? 'admin' : backendMember.role as 'admin' | 'member',
      joinedAt: new Date(backendMember.joined_at),
    }));
  } catch (error) {
    console.error('Error fetching organization members:', error);
    return [];
  }
};

// Note: This function now delegates to userService
export { isUserOrganizationMember };

export const getOrganizationMembersWithUserDetails = async (organizationId: string): Promise<(OrganizationMember & { displayName?: string; email?: string; photoURL?: string })[]> => {
  try {
    const response = await organizationsService.getOrganizationMembers(organizationId);
    
    if (!response.success || !response.data) {
      return [];
    }

    // Backend already includes user details
    return response.data.members.map(backendMember => ({
      id: backendMember.userId,
      organizationId: backendMember.organization_id,
      userId: backendMember.userId,
      role: backendMember.role === 'owner' ? 'admin' : backendMember.role as 'admin' | 'member',
      joinedAt: new Date(backendMember.joinedAt),
      displayName: backendMember.displayName,
      email: backendMember.email,
      photoURL: backendMember.photoURL,
    }));
  } catch (error) {
    console.error('Error fetching organization members with details:', error);
    return [];
  }
};


export const getUserOrganizations = async (_userId: string): Promise<Organization[]> => {
  try {
    // Note: Backend API only supports getting current user's organizations
    // For getting other users' organizations, this would need a different approach
    const response = await organizationsService.getUserOrganizations();
    
    if (!response.success || !response.data) {
      return [];
    }
    
    // Map backend organizations to frontend format
    return response.data.organizations.map(mapBackendOrganizationToFrontend);
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    return [];
  }
};

// Note: This function now delegates to userService
export { getUserRoleInOrganization };

export const removeOrganizationMember = async (
  organizationId: string,
  userId: string
): Promise<void> => {
  try {
    const response = await organizationsService.removeMember(organizationId, userId);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to remove organization member');
    }
  } catch (error) {
    console.error('Error removing organization member:', error);
    throw error;
  }
};

// Organization Invitation operations
export const createOrganizationInvitation = async (
  organizationId: string,
  email: string,
  role: 'admin' | 'member',
  invitedBy: string
): Promise<OrganizationInvitation> => {
  try {
    const response = await organizationsService.createInvitation(organizationId, {
      email,
      role
    });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create organization invitation');
    }

    // Map backend invitation to frontend format
    const backendInvitation = response.data;
    return {
      id: backendInvitation.email,
      organizationId: backendInvitation.organization_id,
      email: backendInvitation.email,
      role: backendInvitation.role,
      invitedBy: backendInvitation.created_by || invitedBy,
      status: 'pending', // Backend might use different status values
      token: backendInvitation.token,
      createdAt: new Date(backendInvitation.created_at),
    } as OrganizationInvitation;
  } catch (error) {
    console.error('Error creating organization invitation:', error);
    throw error;
  }
};

export const getOrganizationInvitations = async (organizationId: string): Promise<OrganizationInvitation[]> => {
  try {
    const response = await organizationsService.getInvitations(organizationId);
    
    if (!response.success || !response.data) {
      return [];
    }

    // Map backend invitations to frontend format
    return response.data.invitations.map(backendInvitation => ({
      id: backendInvitation.email,
      organizationId: backendInvitation.organization_id,
      email: backendInvitation.email,
      role: backendInvitation.role,
      invitedBy: backendInvitation.created_by || '',
      status: 'pending', // Backend might use different status values
      token: backendInvitation.token,
      createdAt: new Date(backendInvitation.created_at),
    }));
  } catch (error) {
    console.error('Error fetching organization invitations:', error);
    return [];
  }
};

export const getPendingInvitationByEmail = async (email: string): Promise<OrganizationInvitation | null> => {
  try {
    const response = await organizationsService.getPendingInvitationsByEmail(email);
    
    if (!response.success || !response.data || response.data.invitations.length === 0) {
      return null;
    }

    // Return the first pending invitation (there should typically be only one)
    const backendInvitation = response.data.invitations[0];
    return {
      id: backendInvitation.email,
      organizationId: backendInvitation.organization_id,
      email: backendInvitation.email,
      role: backendInvitation.role,
      invitedBy: backendInvitation.created_by || '',
      status: 'pending',
      token: backendInvitation.token,
      createdAt: new Date(backendInvitation.created_at),
    };
  } catch (error) {
    console.error('Error fetching pending invitation by email:', error);
    return null;
  }
};

export const acceptOrganizationInvitation = async (
  invitationId: string,
  _userId: string // Not used - backend handles user assignment
): Promise<void> => {
  try {
    // Get the invitation details first
    const invitation = await getPendingInvitationByEmail(invitationId);
    
    if (!invitation) {
      throw new Error('Invitation not found');
    }
    
    const response = await organizationsService.acceptInvitation(invitation.organizationId, {
      token: invitation.token
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to accept organization invitation');
    }
  } catch (error) {
    console.error('Error accepting organization invitation:', error);
    throw error;
  }
};

// Accept invitation by token (for link-based invitations)
export const acceptOrganizationInvitationByToken = async (
  token: string,
  _userId: string, // Not used - backend handles user assignment
  userEmail: string
): Promise<OrganizationInvitation> => {
  try {
    const response = await organizationsService.acceptInvitationByToken({
      token,
      email: userEmail
    });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to accept invitation by token');
    }

    // Map backend member response to invitation format for compatibility
    const backendMember = response.data;
    return {
      id: userEmail,
      organizationId: backendMember.organization_id,
      email: userEmail,
      role: backendMember.role,
      invitedBy: '',
      status: 'accepted',
      token,
      createdAt: new Date(backendMember.joined_at),
    } as OrganizationInvitation;
  } catch (error) {
    console.error('Error accepting organization invitation by token:', error);
    throw error;
  }
};

// Validate invitation token (for signup validation)
export const validateInvitationToken = async (token: string): Promise<{ valid: boolean; invitation?: OrganizationInvitation }> => {
  try {
    const response = await organizationsService.validateInvitation(token, '');
    
    if (!response.success || !response.data) {
      return { valid: false };
    }

    if (response.data.valid) {
      // If we have a valid invitation, try to get the full invitation details
      const invitation = await getInvitationByToken(token);
      return {
        valid: true,
        invitation: invitation || undefined
      };
    }
    
    return { valid: false };
  } catch (error) {
    console.error('Error validating invitation token:', error);
    return { valid: false };
  }
};

// Get invitation by token (for displaying invitation details)
export const getInvitationByToken = async (token: string): Promise<OrganizationInvitation | null> => {
  try {
    const response = await organizationsService.validateInvitation(token, '');
    
    if (!response.success || !response.data || !response.data.valid) {
      return null;
    }

    // Create a mock invitation from the validation response
    // Note: This is limited by the backend validation response structure
    return {
      id: 'unknown', // We don't have access to the email from validation
      organizationId: 'unknown', // We don't have access to org ID from validation
      email: 'unknown',
      role: (response.data.role as 'admin' | 'member') || 'member',
      invitedBy: response.data.inviter_email || '',
      status: 'pending',
      token,
      createdAt: new Date(),
    } as OrganizationInvitation;
  } catch (error) {
    console.error('Error fetching invitation by token:', error);
    return null;
  }
};

export const revokeOrganizationInvitation = async (organizationId: string, email: string): Promise<void> => {
  try {
    const response = await organizationsService.revokeInvitation(organizationId, email);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to revoke organization invitation');
    }
  } catch (error) {
    console.error('Error revoking organization invitation:', error);
    throw error;
  }
};

// Organization Logo operations
export const uploadOrganizationLogo = async (
  organizationId: string,
  file: File
): Promise<string> => {
  try {
    const response = await organizationsService.uploadLogo(organizationId, file);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to upload organization logo');
    }
    
    return response.data.logo_url;
  } catch (error) {
    console.error('Error uploading organization logo:', error);
    throw error;
  }
};

export const deleteOrganizationLogo = async (organizationId: string): Promise<void> => {
  try {
    // Remove logo URL from organization - backend will handle file deletion
    await updateOrganization(organizationId, { logoUrl: undefined });
  } catch (error) {
    console.error('Error deleting organization logo:', error);
    throw error;
  }
};

// Supabase invitation acceptance
export const acceptInvitationSupabase = async (
  email: string,
  orgId: string,
  role: string,
  accessToken: string
): Promise<Organization> => {
  try {
    const response = await organizationsService.acceptInvitationSupabase(email, orgId, role, accessToken);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to accept invitation');
    }
    
    return mapBackendOrganizationToFrontend(response.data);
  } catch (error) {
    console.error('Error accepting invitation via Supabase:', error);
    throw error;
  }
};